import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from '@mui/material';
import { TreeItem, TreeItemProps } from '@mui/x-tree-view';
import { useField } from 'formik';
import { findIndex } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';

import { FormikTreeProps } from './FormikTree';

type FormikTreeItemComponentProps = {
  item: TreeItemProps;
} & Pick<FormikTreeProps, 'renderExtraLabelContent'>;

type AccordionFormikTreeItemComponentProps = FormikTreeItemComponentProps &
  Pick<
    FormikTreeProps,
    'fieldName' | 'fieldValueIdPropertyName' | 'additionalItemSettings'
  >;

export const AccordionFormikTreeItem: React.FC<
  AccordionFormikTreeItemComponentProps
> = ({
  additionalItemSettings: { component: AdditionalSettingsComponent } = {},
  item,
  renderExtraLabelContent,
  fieldName,
  fieldValueIdPropertyName = 'id',
}) => {
  const [expanded, setExpanded] = useState(false);

  const [{ value: fieldValue }] = useField(fieldName);
  const getItemIndex = useCallback(
    (itemId: string) =>
      findIndex(fieldValue, { [fieldValueIdPropertyName]: itemId }),
    [fieldValue, fieldValueIdPropertyName],
  );

  const handleChange = useCallback(
    (e: React.SyntheticEvent, expanded: boolean) => {
      e.stopPropagation();
      setExpanded(expanded);
    },
    [],
  );

  const handleClick = useCallback((e: React.SyntheticEvent) => {
    e.stopPropagation();
  }, []);

  const itemIndex = useMemo(
    () => getItemIndex(item.itemId),
    [getItemIndex, item],
  );

  return (
    <TreeItem
      {...item}
      sx={{
        '& .MuiTreeItem-content': {
          display: 'flex',
          alignItems: 'flex-start',
        },
        '& .MuiTreeItem-checkbox': {
          transition:
            'margin-top 0.1s ease-in-out, margin-bottom 0.1s ease-in-out',
          marginTop: expanded ? '20px' : '12px',
          marginBottom: expanded ? '20px' : '12px',
        },
      }}
      slotProps={{
        label: {
          style: {
            cursor: 'pointer',
            userSelect: 'none',
          },

          children: (
            <Accordion
              style={{
                background: 'transparent',
                cursor: 'pointer',
                userSelect: 'none',
              }}
              expanded={expanded}
              onChange={handleChange}
              onClick={handleClick}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                id={item.itemId}
                aria-controls={item.itemId}
              >
                {item.label}
                {renderExtraLabelContent && renderExtraLabelContent(item)}
              </AccordionSummary>
              <AccordionDetails>
                {AdditionalSettingsComponent && (
                  <AdditionalSettingsComponent
                    item={item}
                    index={itemIndex}
                    disabled={itemIndex < 0} // disabled if the item is not selected
                  />
                )}
              </AccordionDetails>
            </Accordion>
          ),
        },
      }}
    />
  );
};

export const BasicFormikTreeItem = React.memo<FormikTreeItemComponentProps>(
  ({ renderExtraLabelContent, item }) => {
    const renderLabel = useMemo(
      () =>
        ({ children }: { children: React.ReactNode }) =>
          (
            <Typography
              sx={{
                margin: '8px',
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              {children}
              {renderExtraLabelContent && renderExtraLabelContent(item)}
            </Typography>
          ),
      [renderExtraLabelContent, item],
    );

    return (
      <TreeItem
        {...item}
        slots={{
          label: renderLabel,
        }}
      />
    );
  },
);
